import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  FormHelperText,
   Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,InputLabel,Select,MenuItem
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const VideoUploaded = () => {
  const [therapyVideo, setTherapyVideo] = useState({
    opNumber: '',
    caseName: '',
    studentClinician: '',
    date: '',
    videoFile: null,
    description: ''
  });
 
  const [loading, setLoading] = useState(false);
      const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
      const [openPasswordModal, setOpenPasswordModal] = useState(false);
      const [password, setPassword] = useState('');
      const [downloadFormat, setDownloadFormat] = useState('excel');
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!therapyVideo.opNumber.trim()) {
      newErrors.opNumber = 'IP/OP/Internal No is required';
    }
    if (!therapyVideo.caseName.trim()) {
      newErrors.caseName = 'Case Name is required';
    }
    if (!therapyVideo.studentClinician.trim()) {
      newErrors.studentClinician = 'Student Clinician is required';
    }
    if (!therapyVideo.date) {
      newErrors.date = 'Date is required';
    }
    if (!therapyVideo.videoFile) {
      newErrors.videoFile = 'Video file is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTherapyVideoChange = (field, value) => {
    setTherapyVideo(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
 const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Therapy Videos ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "therapy_videos",
          formName: "Therapy_Videos",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Therapy_Videos_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };
  const handleTherapyVideoSubmit = async () => {
    if (!validateForm()) {
      setAlert({ 
        open: true, 
        message: 'Please fix the errors in the form.', 
        severity: 'error' 
      });
      return;
    }

    setLoading(true);
    setAlert({ open: false, message: '', severity: 'success' });
    
    try {
      const formData = new FormData();
      formData.append('op_number', therapyVideo.opNumber);
      formData.append('case_name', therapyVideo.caseName);
      formData.append('student_clinician', therapyVideo.studentClinician);
      formData.append('date', therapyVideo.date);
      formData.append('description', therapyVideo.description);
      formData.append('video_file', therapyVideo.videoFile);

      console.log('Submitting form data...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/therapy-videos`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header for FormData - browser will set it automatically with boundary
      });

      console.log('Response status:', response.status);
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error(`Server returned non-JSON response. Status: ${response.status}`);
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `Server error: ${response.status}`);
      }

      setAlert({ 
        open: true, 
        message: result.message || 'Therapy video uploaded successfully!', 
        severity: 'success' 
      });
      
      // Reset form
      setTherapyVideo({
        opNumber: '',
        caseName: '',
        studentClinician: '',
        date: '',
        videoFile: null,
        description: ''
      });
      setErrors({});

    } catch (error) {
      console.error('Upload error:', error);
      setAlert({ 
        open: true, 
        message: error.message || 'Failed to upload therapy video. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('video/')) {
        setErrors(prev => ({ ...prev, videoFile: 'Please select a video file' }));
        return;
      }
      
      // Check file size (500MB limit)
      if (file.size > 500 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, videoFile: 'File size must be less than 500MB' }));
        return;
      }
      
      handleTherapyVideoChange('videoFile', file);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, margin: 'auto' }}>
        <Box sx={{ position: 'absolute', top: 300, right: 190 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
        Therapy Video Upload
      </Typography>

      {alert.open && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert({ ...alert, open: false })} 
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" sx={{ mb: 3 }}>
          Upload Therapy Video
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.opNumber}>
              <TextField
                required
                label="IP/OP/Internal No"
                value={therapyVideo.opNumber}
                onChange={(e) => handleTherapyVideoChange('opNumber', e.target.value)}
                error={!!errors.opNumber}
              />
              {errors.opNumber && <FormHelperText>{errors.opNumber}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.caseName}>
              <TextField
                required
                label="Case Name"
                value={therapyVideo.caseName}
                onChange={(e) => handleTherapyVideoChange('caseName', e.target.value)}
                error={!!errors.caseName}
              />
              {errors.caseName && <FormHelperText>{errors.caseName}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.studentClinician}>
              <TextField
                required
                label="Student Clinician"
                value={therapyVideo.studentClinician}
                onChange={(e) => handleTherapyVideoChange('studentClinician', e.target.value)}
                error={!!errors.studentClinician}
              />
              {errors.studentClinician && <FormHelperText>{errors.studentClinician}</FormHelperText>}
            </FormControl>
          </Grid>
               <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={therapyVideo.description}
              onChange={(e) => handleTherapyVideoChange('description', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.date}>
              <TextField
                required
                label="Date"
                type="date"
                value={therapyVideo.date}
                onChange={(e) => handleTherapyVideoChange('date', e.target.value)}
                error={!!errors.date}
                InputLabelProps={{ shrink: true }}
              />
              {errors.date && <FormHelperText>{errors.date}</FormHelperText>}
            </FormControl>
          </Grid>
                  <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.videoFile}>
              <Button 
                variant="outlined" 
                component="label" 
                fullWidth 
                startIcon={<CloudUploadIcon />}
                sx={{ 
                  py: 1.5, 
                  borderStyle: 'dashed',
                  borderColor: errors.videoFile ? 'error.main' : 'primary.main'
                }}
              >
                {therapyVideo.videoFile ? `Video Selected: ${therapyVideo.videoFile.name}` : 'Upload Therapy Video'}
                <input
                  type="file"
                  hidden
                  accept="video/*"
                  onChange={handleFileChange}
                />
              </Button>
              {errors.videoFile && (
                <FormHelperText error>{errors.videoFile}</FormHelperText>
              )}
              {therapyVideo.videoFile && !errors.videoFile && (
                <FormHelperText>
                  File size: {(therapyVideo.videoFile.size / (1024 * 1024)).toFixed(2)} MB
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          

          
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTherapyVideoSubmit}
              disabled={loading}
              fullWidth
              size="large"
              sx={{ py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Therapy Video'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default VideoUploaded;